import React, { useState } from "react";
import { Heading, Subheading } from "../../../design/heading";
import { Participant } from "../../../models/Participant";
import moment from "moment";
import EmptyStateView from "../../../components/EmptyStateView";
import EmploymentInfoButton from "../../../components/EmploymentInfoButton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../design/table";
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownMenu,
} from "../../../design/dropdown";
import { EllipsisHorizontalIcon } from "@heroicons/react/20/solid";
import UpsellEnrichmentBanner from "./UpsellEnrichmentBanner";
import EmploymentInfoDialog from "../../../components/Dialogs/EmploymentInfoDialog/EmploymentInfoDialog";
import { EmploymentInfo } from "../../../models/EmploymentInfo";

interface ParticipantEmploymentProps {
  participant: Participant;
}

const ParticipantEmployment: React.FC<ParticipantEmploymentProps> = ({
  participant,
}) => {
  const [updateDialogIsOpen, setUpdateDialogIsOpen] = useState(false);
  const [selectedEmployment, setSelectedEmployment] = useState<
    EmploymentInfo | undefined
  >(undefined);
  const [deleteAlertIsOpen, setDeleteAlertIsOpen] = useState(false);

  const employments = (participant.employments || []).sort((a, b) => {
    if (a.startDate && b.startDate) {
      return new Date(b.startDate).getTime() - new Date(a.startDate).getTime();
    }
    if (a.startDate) return -1;
    if (b.startDate) return 1;
    return 0;
  });

  return (
    <div className="mt-8 space-y-2">
      <div className="flex items-center justify-between">
        <Heading>Employment</Heading>
        <EmploymentInfoButton participantId={participant._id} />
      </div>

      {!participant.apolloId && (
        <UpsellEnrichmentBanner participant={participant} />
      )}

      {employments.length === 0 ? (
        <EmptyStateView
          title="No Employment"
          message="This participant has no employment information."
          action={<EmploymentInfoButton participantId={participant._id} />}
        />
      ) : (
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader>Position</TableHeader>
              <TableHeader>Years</TableHeader>
              <TableHeader>Salary</TableHeader>
              <TableHeader />
            </TableRow>
          </TableHead>
          <TableBody>
            {employments.map((employment) => (
              <TableRow key={employment._id}>
                <TableCell>
                  <Subheading>
                    {employment.occupation} @ {employment.employer}
                  </Subheading>
                </TableCell>
                <TableCell>
                  {moment(employment.startDate).format("MM/DD/YYYY")} -{" "}
                  {employment.endDate
                    ? moment(employment.endDate).format("MM/DD/YYYY")
                    : "Present"}
                </TableCell>
                <TableCell>
                  {employment.yearlyIncome ? (
                    <span>${employment.yearlyIncome.toLocaleString()}</span>
                  ) : (
                    <span>Not provided</span>
                  )}
                </TableCell>
                <TableCell>
                  <Dropdown>
                    <DropdownButton plain>
                      <EllipsisHorizontalIcon className="w-4 h-4" />
                    </DropdownButton>
                    <DropdownMenu>
                      <DropdownItem
                        onClick={() => {
                          setSelectedEmployment(employment);
                          setUpdateDialogIsOpen(true);
                        }}
                      >
                        Edit
                      </DropdownItem>
                      <DropdownItem onClick={() => setDeleteAlertIsOpen(true)}>
                        Delete
                      </DropdownItem>
                    </DropdownMenu>
                  </Dropdown>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* Only one dialog, dynamically populated */}
      <EmploymentInfoDialog
        participantId={participant._id}
        isOpen={updateDialogIsOpen}
        setIsOpen={setUpdateDialogIsOpen}
        employmentInfo={selectedEmployment}
      />
    </div>
  );
};

export default ParticipantEmployment;
